// Post.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { withTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import { format, fromUnixTime } from 'date-fns';

import { NULL_FUNCTION } from '../resource/defaults.js';

import WithEditMessage from '../container/WithEditMessage.js';
import WithHideMessage from '../container/WithHideMessage.js';
import UserStatusIndicator from '../container/UserStatusIndicator.js';
import UserAvatarWithStatus from '../container/UserAvatarWithStatus.js';
import PostMedia from '../container/PostMedia.js';
import LinkSubstring from '../container/LinkSubstring.js';
import PostSubscribeAndUnlockButton from '../container/PostSubscribeAndUnlockButton.js';
import FollowButton from '../container/FollowButton.js';
import WithMessageMetaData from '../container/WithMessageMetaData.js';
import WithMessagePinData from '../container/WithMessagePinData.js';

import Loading from '../component/Loading.jsx';
import { SanitizedLink as Link } from '../component/SanitizedLink.jsx';
import MessageCategory from '../container/MessageCategory.js';
import getUserProfileUrl from '../resource/getUserProfileUrl.js';

import formatNumber from '../resource/formatNumber.js';
import getRelativeTime from '../resource/getRelativeTime.js';
import { MessageMoreFeature } from '../resource/messageConstants.js';
import getCanShare from '../resource/getCanShare.js';
import { ProfileListType } from '../resource/profileConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { getIsChinaPartner } from '../resource/partner.js';

import ResetButtonStyle from '../style/ResetButtonStyle.js';
import TextEllipsis from '../style/TextEllipsis.js';
import { color, textColor, breakpoint } from '../style/variables.js';
import media from '../style/media.js';
import { flixPlayerZIndex } from '../style/variables.js';

import MediaLibraryIcon from '../../img/ic_media_library.svg';
import MoreIcon from '../../img/more.svg';
import ChatIcon from '../../img/btn_chat_24.svg';
import LikeIcon from '../../img/btn_rating_like.svg';
import DislikeIcon from '../../img/btn_rating_unlike.svg';
import ActiveLikeIcon from '../../img/btn_rating_like_active.svg';
import ActiveDislikeIcon from '../../img/btn_rating_unlike_active.svg';
import PinnedGreyIconResource from '../../img/ic_pin_grey.svg';
import CopyIcon from '../../img/ic_album.svg';
import ShareIcon from '../../img/ic-share-20.svg';

import { ButtonId } from '../resource/trackingConstants.js';
import getShareLink from '../resource/getShareLink.js';
import withFetchTranslation from '../resource/withFetchTranslation.js';
import { localeConvertor } from '../resource/i18n.js';
import useShortShareLink, { ShareType } from '../hook/useShortShareLink.js';

const MOBILE_CAPTION_LINE_HEIGHT = 18;
const MOBILE_CAPTION_FONT_SIZE = 12;
const CAPTION_LINE_HEIGHT = 24;
const CAPTION_FONT_SIZE = 16;
const CAPTION_LINE_CLAMP = 2; // TODO: remote config

const canShare = getCanShare();
const isChinaPartner = getIsChinaPartner();

// used to run the hook
const PostShareLinkProvider = ({ targetId, onShareUrlReady, children }) => {
  const [isFetching, fetchShortShareLink] = useShortShareLink({
    targetId,
    shareType: ShareType.POST,
    onShortShareLinkFetched: onShareUrlReady,
  });

  return children({ fetchShortShareLink, isFetching });
};

PostShareLinkProvider.propTypes = {
  targetId: PropTypes.string.isRequired,
  onShareUrlReady: PropTypes.func.isRequired,
  children: PropTypes.func.isRequired,
};

export class Post extends React.PureComponent {
  state = {
    shouldShowTranslation: false,
    shouldShowMoreCaption: false,
    shouldShowCaptionMoreButton: false,
    isIntersecting: false,
    isIntersected: false,
    isClient: false,
  };
  nextTick = null;
  captionRef = React.createRef();
  postRef = React.createRef();
  handleShare = NULL_FUNCTION;

  getIsOnMobile = () => {
    const { deviceWidth } = this.props;
    return deviceWidth <= breakpoint.mobile;
  };

  getProfileLink = () => {
    const { senderId, senderName } = this.props;
    return getUserProfileUrl({ userId: senderId, username: senderName });
  };

  getDisplayCaption = () => {
    const { shouldShowTranslation } = this.state;
    const { caption, originalCaptionText } = this.props;
    return shouldShowTranslation ? caption : originalCaptionText;
  };

  scrollIntoView = () => {
    const { shouldScrollIntoView } = this.props;
    if (!this.postRef.current || !shouldScrollIntoView) {
      return;
    }
    return this.postRef.current.scrollIntoView({
      block: 'center',
      behavior: 'auto',
    });
  };

  handleIntersection = ({ isIntersecting }) => {
    this.setState({ isIntersecting });
    if (isIntersecting) {
      this.setState({
        isIntersected: true,
      });
    }
  };

  setShouldShowCaptionMoreButton = () => {
    const caption = this.getDisplayCaption();
    if (!this.captionRef.current || !caption) {
      return this.setState({
        shouldShowCaptionMoreButton: false,
      });
    }
    const lineNumber = (caption || '').split('\n').length - 1;
    if (lineNumber >= CAPTION_LINE_CLAMP) {
      return this.setState({
        shouldShowCaptionMoreButton: true,
      });
    }
    const isOnMobile = this.getIsOnMobile();
    const containerWidth = this.captionRef.current.offsetWidth;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = `${
      isOnMobile ? MOBILE_CAPTION_FONT_SIZE : CAPTION_FONT_SIZE
    }px sans-serif`;
    const captionWidth = context.measureText(caption).width;
    this.setState({
      shouldShowCaptionMoreButton:
        Math.ceil(captionWidth / containerWidth) > CAPTION_LINE_CLAMP,
    });
  };

  getShareTitleKey = () => {
    const { shareI18nPhotoTemplates } = this.props;
    const randomIndex = Math.floor(
      Math.random() * shareI18nPhotoTemplates.length
    );
    return shareI18nPhotoTemplates[randomIndex];
  };

  getShareTranslationOptions = () => {
    const { senderName, referralCode, referralSignupRewardDiamond } =
      this.props;

    return {
      ns: TranslationNamespace.GENERAL,
      username: senderName,
      referral_code: referralCode || '',
      reward_diamond: referralSignupRewardDiamond || 0,
    };
  };

  // Hook input handler
  handleShareUrlReady = finalUrl => {
    const { t, copyTextWithToastr } = this.props;

    if (canShare) {
      const shareText = t(
        this.getShareTitleKey(),
        this.getShareTranslationOptions()
      );
      navigator
        .share({
          title: 'SWAG',
          text: shareText,
          url: finalUrl || location.href,
        })
        .catch(() => null);
    } else {
      copyTextWithToastr({
        copiedText: finalUrl || window.location.href,
        successfulTextKey: 'copy_to_clipboard_successfully',
        failedTextKey: 'copy_to_clipboard_failed',
        textKeyNamespace: TranslationNamespace.NOTIFICATION,
      });
    }
  };

  fetchData() {
    const {
      messageId,
      senderId,
      senderName,
      likeCount,
      viewCount,
      unlockPrice,
      assetIds,
      fetchMessageDetail,
      fetchUser,
      fetchRating,
    } = this.props;
    const { isIntersected } = this.state;
    if (messageId && isIntersected) {
      if (
        (!likeCount && !viewCount) ||
        !senderId ||
        unlockPrice == null ||
        assetIds.length === 0
      ) {
        fetchMessageDetail({ messageId });
      }
      fetchRating({ id: messageId });
    }
    if (senderId && !senderName) {
      fetchUser({ id: senderId });
    }
  }

  checkAndFetchMeReferral = () => {
    const { shouldFetchReferral, fetchMeReferral } = this.props;
    if (shouldFetchReferral) {
      fetchMeReferral();
    }
  };

  componentDidMount() {
    this.nextTick = setTimeout(() => {
      this.fetchData();
      this.scrollIntoView();
      this.setState({
        isClient: true,
      });
    });
    this.setShouldShowCaptionMoreButton();

    this.checkAndFetchMeReferral();
  }

  componentDidUpdate(prevProps, prevState) {
    const { shouldShowTranslation, isIntersected, shouldShowMoreCaption } =
      this.state;
    const {
      originalTitle,
      originalCaptionText,
      title,
      caption,
      messageId,
      senderId,
      deviceWidth,
      messageHeight,
      canViewMedia,
      isFairPlay,
      shouldPrefetch,
      shouldScrollIntoView,
      fetchMessageSegments,
    } = this.props;
    const isMessageIdChanged = prevProps.messageId !== messageId;
    const isSenderIdChanged = prevProps.senderId !== senderId;
    const isIntersectedChanged = isIntersected !== prevState.isIntersected;
    const isCanViewMediaChanged = prevProps.canViewMedia !== canViewMedia;
    const isOriginalTitleChanged = originalTitle !== prevProps.originalTitle;
    const isOriginalCaptionChanged =
      originalCaptionText !== prevProps.originalCaptionText;
    const isShouldShowTranslationChanged =
      shouldShowTranslation !== prevState.shouldShowTranslation;
    const isTitleChanged = title !== prevProps.title;
    const isCaptionChanged = caption !== prevProps.caption;
    const isDeviceWidthChanged = deviceWidth !== prevProps.deviceWidth;
    const isMessageHeightChanged = messageHeight !== prevProps.messageHeight;
    const isShouldPrefetchChanged = shouldPrefetch !== prevProps.shouldPrefetch;
    const isShouldScrollIntoViewChanged =
      shouldScrollIntoView !== prevProps.shouldScrollIntoView;
    if (
      isMessageIdChanged ||
      isSenderIdChanged ||
      isIntersectedChanged ||
      isCanViewMediaChanged
    ) {
      clearTimeout(this.nextTick);
      this.nextTick = setTimeout(() => this.fetchData());
    }
    if (isShouldPrefetchChanged && shouldPrefetch) {
      fetchMessageSegments({
        messageId,
        manifestType: isFairPlay ? 'm3u8' : 'mpd',
      });
    }
    if (
      isIntersected &&
      !shouldShowMoreCaption &&
      (isOriginalCaptionChanged ||
        isCaptionChanged ||
        isOriginalTitleChanged ||
        isTitleChanged ||
        isShouldShowTranslationChanged ||
        isDeviceWidthChanged ||
        isIntersectedChanged)
    ) {
      this.setShouldShowCaptionMoreButton();
    }
    if (isMessageHeightChanged || isShouldScrollIntoViewChanged) {
      this.scrollIntoView();
    }
  }

  componentWillUnmount() {
    clearTimeout(this.nextTick);
  }

  renderUserRow() {
    const {
      shouldShowChatroomMediaLibrary,
      isChatroomMessageLayout,
      isPublished,
      chatroomId,
      meId,
      senderId,
      messageId,
      displayedUsername,
      senderName,
      postedAtUnix,
      scheduledAtUnix,
      unlockPrice,
      subscriptionProductIds,
      toggleModal,
      t,
    } = this.props;
    const { isClient } = this.state;
    if (isChatroomMessageLayout && this.getIsOnMobile()) return null;

    const isSender = meId && senderId && senderId === meId;
    const relativeTime = getRelativeTime({ unixTimestamp: postedAtUnix });
    const shouldShowSubscription =
      subscriptionProductIds.length > 0 && !!unlockPrice;

    return (
      <UserRow>
        <AvatarCol>
          <UserAvatarWithStatus id={senderId} size={36} circleOffset={2} />
        </AvatarCol>
        <UsernameCol>
          <NicknameRow>
            <NicknameLink
              to={this.getProfileLink()}
              data-element_id={ButtonId.All.ButtonUsername}
              data-tracking_payload={{
                messageId,
              }}
            >
              <Nickname>{displayedUsername || `@${senderName}`}</Nickname>
              {displayedUsername && <Username>@{senderName}</Username>}
              <UserStatusIndicatorWrapper>
                <UserStatusIndicator
                  userId={senderId}
                  shouldShowOnline
                  shouldShowLiveStream
                />
              </UserStatusIndicatorWrapper>
            </NicknameLink>
          </NicknameRow>
          <DateRow>
            {!isPublished ? (
              <span>
                {t('schedule_time', {
                  ns: TranslationNamespace.POST,
                  date:
                    /* render formated time on SSR will causing hydration fail due to timezone */
                    isClient && scheduledAtUnix
                      ? format(
                          fromUnixTime(scheduledAtUnix),
                          'yyyy-MM-dd HH:mm'
                        )
                      : '',
                })}
              </span>
            ) : (
              <span>{relativeTime}</span>
            )}
            {shouldShowSubscription && (
              <span>
                ．
                {t('subscribe_only', {
                  ns: TranslationNamespace.POST,
                })}
              </span>
            )}
          </DateRow>
        </UsernameCol>
        <ActionCol>
          {!isSender && <FollowButton userId={senderId} />}
          {isChatroomMessageLayout ? (
            shouldShowChatroomMediaLibrary ? (
              <ContextMenuButton
                aria-label="navigate to media library"
                as={Link}
                to={`/chat/${chatroomId}/media`}
                data-element_id={ButtonId.Chatroom.ButtonMediaLibrary}
                data-tracking_payload={{
                  messageId,
                }}
              >
                <img src={MediaLibraryIcon} aria-hidden />
              </ContextMenuButton>
            ) : null
          ) : isPublished ? (
            <ContextMenuButton
              aria-label="show more actions"
              data-element_id={ButtonId.All.ButtonMore}
              data-tracking_payload={{
                messageId,
              }}
              onClick={() => {
                return toggleModal({
                  modalId: 'MessageMoreModal',
                  payload: {
                    messageId,
                    pinProfileListType: ProfileListType.IMAGE,
                    features: [
                      ...(meId ? [MessageMoreFeature.BLOCK] : []),
                      MessageMoreFeature.FOLLOW,
                      MessageMoreFeature.PROFILE,
                      MessageMoreFeature.PIN,
                      MessageMoreFeature.EDIT,
                      MessageMoreFeature.HIDE,
                    ],
                    shareLink: getShareLink({
                      pathname: `/post/${messageId}`,
                    }),
                  },
                  isOpen: true,
                });
              }}
            >
              <img src={MoreIcon} aria-hidden />
            </ContextMenuButton>
          ) : null}
        </ActionCol>
      </UserRow>
    );
  }

  renderPinnedRow = () => {
    const { t, messageId } = this.props;
    return (
      <WithMessagePinData
        messageId={messageId}
        profileListType={ProfileListType.IMAGE}
      >
        {({ isPinned }) =>
          isPinned ? (
            <PinnedRow>
              <PinIcon src={PinnedGreyIconResource} width="16" height="16" />
              <PinText>
                {t('pinned_post', {
                  ns: TranslationNamespace.POST,
                })}
              </PinText>
            </PinnedRow>
          ) : null
        }
      </WithMessagePinData>
    );
  };

  renderCaptionRow() {
    const {
      shouldShowTranslation,
      shouldShowMoreCaption,
      shouldShowCaptionMoreButton,
    } = this.state;
    const {
      originalTitle,
      originalCaptionText,
      t,
      sourceLanguage,
      currentLanguage,
      messageId,
    } = this.props;
    const isLanguageMatched =
      currentLanguage ===
      localeConvertor({
        locale: sourceLanguage,
      });
    const displayCaption = this.getDisplayCaption();
    return (
      <CaptionRow>
        <Caption
          ref={this.captionRef}
          shouldShowMoreCaption={shouldShowMoreCaption}
          isOnMobile={this.getIsOnMobile()}
        >
          <LinkSubstring>{displayCaption}</LinkSubstring>
        </Caption>
        {shouldShowCaptionMoreButton && (
          <CaptionMoreButton
            data-element_id={ButtonId.Post.ButtonPostTextMore}
            data-tracking_payload={{
              messageId,
            }}
            onClick={() => {
              this.setState({
                shouldShowMoreCaption: true,
                shouldShowCaptionMoreButton: false,
              });
            }}
          >
            ...{' '}
            {t('more', {
              ns: TranslationNamespace.GENERAL,
            })}
          </CaptionMoreButton>
        )}
        <MessageCategory messageId={messageId} styles={{ paddingTop: '8px' }} />
        {(originalTitle || originalCaptionText) &&
          sourceLanguage &&
          !isLanguageMatched && (
            <TranslationToggler
              data-element_id={
                shouldShowTranslation
                  ? ButtonId.All.ButtonTextOriginal
                  : ButtonId.All.ButtonTextTranslate
              }
              data-tracking_payload={{
                messageId,
              }}
              onClick={() => {
                return this.setState({
                  shouldShowTranslation: !shouldShowTranslation,
                });
              }}
            >
              {t(shouldShowTranslation ? 'show_original' : 'show_translate', {
                ns: TranslationNamespace.POST,
              })}
            </TranslationToggler>
          )}
      </CaptionRow>
    );
  }

  renderMediaRow() {
    const { messageId, shouldShowMedias, aspectRatio, isOnPostFeed } =
      this.props;
    const { isIntersecting, isIntersected } = this.state;
    if (!shouldShowMedias) return null;

    return (
      <PostMediaWrapper isOnPostFeed={isOnPostFeed} aspectRatio={aspectRatio}>
        <PostMedia
          postId={messageId}
          isIntersected={isIntersected}
          isIntersecting={isIntersecting}
          isOnMobile={this.getIsOnMobile()}
          loadMode={this.props.loadMode}
        />
      </PostMediaWrapper>
    );
  }

  renderInteractionRow() {
    const {
      meId,
      senderId,
      messageId,
      ratedAs,
      submitRating,
      sendChatStart,
      toggleModal,
      canViewMedia,
      isFetchingShortShareLink,
      isChatroomMessageLayout,
      isPublished,
      t,
    } = this.props;
    const isSender = meId && senderId && senderId === meId;
    const isDisabled = !canViewMedia && meId;
    if (!isPublished) {
      return (
        <Interactions>
          <WithHideMessage messageId={messageId}>
            {({ handleOnClick, iconSource }) => (
              <Interaction
                data-element_id={ButtonId.All.ButtonHiddenMassage}
                data-tracking_payload={{
                  'message.id': messageId,
                }}
                onClick={handleOnClick}
              >
                <img
                  src={iconSource}
                  title={t('hide', {
                    ns: TranslationNamespace.POST,
                  })}
                />
              </Interaction>
            )}
          </WithHideMessage>
          <Placeholder />
          <WithEditMessage messageId={messageId}>
            {({ handleOnClick }) => {
              return (
                <MessageButton
                  data-element_id={ButtonId.All.ButtonEdit}
                  data-tracking_payload={{
                    'message.id': messageId,
                  }}
                  onClick={handleOnClick}
                >
                  {t('edit', {
                    ns: TranslationNamespace.POST,
                  })}
                </MessageButton>
              );
            }}
          </WithEditMessage>
        </Interactions>
      );
    }
    return (
      <Interactions>
        {!isSender && (
          <>
            <Interaction
              disabled={isDisabled}
              data-element_id={ButtonId.All.ButtonLike}
              data-tracking_payload={{
                messageId,
              }}
              onClick={() => {
                submitRating({ id: messageId, rating: 100 });
              }}
            >
              <img
                src={ratedAs === 100 ? ActiveLikeIcon : LikeIcon}
                title="click to like"
              />
            </Interaction>
            <Interaction
              disabled={isDisabled}
              data-element_id={ButtonId.All.ButtonDislike}
              data-tracking_payload={{
                messageId,
              }}
              onClick={() => {
                return submitRating({ id: messageId, rating: 0 });
              }}
            >
              <img
                src={ratedAs === 0 ? ActiveDislikeIcon : DislikeIcon}
                title="click to dislike"
              />
            </Interaction>
          </>
        )}
        {!isChinaPartner && !isChatroomMessageLayout && (
          <Interaction
            data-element_id={
              canShare ? ButtonId.All.ButtonShare : ButtonId.All.ButtonCopyLink
            }
            data-tracking_payload={{
              messageId,
            }}
            onClick={this.handleShare}
          >
            {canShare ? (
              <img
                src={ShareIcon}
                alt={t('general_share', {
                  ns: TranslationNamespace.GENERAL,
                })}
              />
            ) : isFetchingShortShareLink ? (
              <Loading />
            ) : (
              <img
                src={CopyIcon}
                alt={t('general_copy', {
                  ns: TranslationNamespace.GENERAL,
                })}
              />
            )}
          </Interaction>
        )}
        <Placeholder />
        {!isChatroomMessageLayout && (
          <MessageButton
            data-element_id={
              isSender ? ButtonId.Post.ButtonSummary : ButtonId.All.ButtonChat
            }
            data-tracking_payload={{
              messageId,
            }}
            onClick={() => {
              if (isSender) {
                return toggleModal({
                  modalId: 'PostSummaryModal',
                  payload: {
                    postId: messageId,
                  },
                  isOpen: true,
                });
              }
              return sendChatStart({ userId: senderId });
            }}
          >
            {isSender ? (
              t('post_summary', {
                ns: TranslationNamespace.POST,
              })
            ) : (
              <>
                <img
                  src={ChatIcon}
                  alt={t('start_chat_prompt', {
                    ns: TranslationNamespace.POST,
                  })}
                  aria-hidden
                />
                {t('start_chat_prompt', {
                  ns: TranslationNamespace.POST,
                })}
              </>
            )}
          </MessageButton>
        )}
      </Interactions>
    );
  }

  renderMetaRow() {
    const { messageId, isChatroomMessageLayout, t } = this.props;
    if (isChatroomMessageLayout) return null;
    return (
      <WithMessageMetaData messageId={messageId}>
        {({ likes, viewCount }) => {
          const hasLikes = !!likes;
          const hasViewCount = '-' !== viewCount;
          if (!hasLikes && !hasViewCount) {
            return null;
          }
          return (
            <Metas>
              {hasLikes && (
                <Meta>{`${likes} ${t('likes', {
                  ns: TranslationNamespace.POST,
                })}`}</Meta>
              )}
              {hasLikes && hasViewCount && <Divider />}
              {hasViewCount && (
                <Meta>
                  {t('view_count_format', {
                    ...[formatNumber(viewCount)],
                    ns: TranslationNamespace.POST,
                  })}
                </Meta>
              )}
            </Metas>
          );
        }}
      </WithMessageMetaData>
    );
  }

  renderUnlockAndSubscribe = () => {
    const { messageId, shouldShowSubscribeAndUnlock } = this.props;
    const { isIntersected } = this.state;
    if (!shouldShowSubscribeAndUnlock) {
      return null;
    }
    return (
      <ActionButtons>
        <PostSubscribeAndUnlockButton
          postId={messageId}
          isIntersected={isIntersected}
        />
      </ActionButtons>
    );
  };

  render() {
    const {
      messageHeight,
      shouldShowMedias,
      mediaHeightInPostFeed,
      isOnPostFeed,
      messageId,
    } = this.props;
    const { isIntersecting } = this.state;
    return (
      <>
        <PostShareLinkProvider
          targetId={messageId}
          onShareUrlReady={this.handleShareUrlReady}
        >
          {({ fetchShortShareLink }) => {
            this.handleShare = fetchShortShareLink;
            return null;
          }}
        </PostShareLinkProvider>

        <ObservedPost
          onChange={this.handleIntersection}
          postRef={this.postRef}
          messageHeight={messageHeight}
          shouldShowMedias={shouldShowMedias}
          isOnPostFeed={isOnPostFeed}
          isIntersecting={isIntersecting}
          mediaHeightInPostFeed={mediaHeightInPostFeed}
        >
          {this.renderUserRow()}
          {this.renderPinnedRow()}
          {this.renderCaptionRow()}
          <MediasWrapper>
            {this.renderMediaRow({ isIntersecting })}
            {this.renderUnlockAndSubscribe()}
          </MediasWrapper>
          {this.renderInteractionRow()}
          {this.renderMetaRow()}
        </ObservedPost>
      </>
    );
  }
}

Post.propTypes = {
  chatroomId: PropTypes.string,
  messageId: PropTypes.string,
  senderId: PropTypes.string,
  senderName: PropTypes.string,
  displayedUsername: PropTypes.string,
  meId: PropTypes.string,
  postedAtUnix: PropTypes.number,
  scheduledAtUnix: PropTypes.number,
  title: PropTypes.string,
  originalTitle: PropTypes.string,
  caption: PropTypes.string,
  originalCaptionText: PropTypes.string,
  aspectRatio: PropTypes.string,
  likeCount: PropTypes.number,
  viewCount: PropTypes.number,
  unlockPrice: PropTypes.number,
  deviceWidth: PropTypes.number,
  messageHeight: PropTypes.number,
  mediaHeightInPostFeed: PropTypes.number,
  ratedAs: PropTypes.number,
  canViewMedia: PropTypes.bool,
  isFairPlay: PropTypes.bool,
  shouldPrefetch: PropTypes.bool,
  loadMode: PropTypes.string,
  isChatroomMessageLayout: PropTypes.bool,
  shouldShowSubscribeAndUnlock: PropTypes.bool,
  shouldScrollIntoView: PropTypes.bool,
  isOnPostFeed: PropTypes.bool,
  isFetchingShortShareLink: PropTypes.bool,
  shouldShowChatroomMediaLibrary: PropTypes.bool,
  isPublished: PropTypes.bool,
  sourceLanguage: PropTypes.string,
  currentLanguage: PropTypes.string,
  assetIds: PropTypes.array,
  subscriptionProductIds: PropTypes.array,
  referralCode: PropTypes.string,
  referralSignupRewardDiamond: PropTypes.number,
  shouldFetchReferral: PropTypes.bool,
  fetchMessageSegments: PropTypes.func,
  fetchMessageDetail: PropTypes.func,
  fetchUser: PropTypes.func,
  fetchRating: PropTypes.func,
  submitRating: PropTypes.func,
  sendChatStart: PropTypes.func,
  toggleModal: PropTypes.func,
  copyTextWithToastr: PropTypes.func,
  fetchMeReferral: PropTypes.func,
  t: PropTypes.func.isRequired,
  shouldShowMedias: PropTypes.bool,
  shareI18nPhotoTemplates: PropTypes.array,
};

Post.defaultProps = {
  messageId: '',
  chatroomId: '',
  senderId: '',
  senderName: '',
  displayedUsername: '',
  meId: null,
  postedAtUnix: 0,
  scheduledAtUnix: 0,
  title: '',
  originalTitle: '',
  caption: '',
  originalCaptionText: '',
  likeCount: 0,
  viewCount: 0,
  unlockPrice: null,
  deviceWidth: 0,
  aspectRatio: null,
  messageHeight: null,
  mediaHeightInPostFeed: null,
  ratedAs: null,
  canViewMedia: false,
  isFairPlay: false,
  shouldPrefetch: false,
  isChatroomMessageLayout: false,
  shouldShowSubscribeAndUnlock: false,
  shouldScrollIntoView: false,
  isOnPostFeed: false,
  isFetchingShortShareLink: false,
  shouldShowChatroomMediaLibrary: false,
  isPublished: true,
  sourceLanguage: '',
  currentLanguage: '',
  assetIds: [],
  subscriptionProductIds: [],
  referralCode: '',
  referralSignupRewardDiamond: 0,
  fetchMessageSegments: NULL_FUNCTION,
  fetchMessageDetail: NULL_FUNCTION,
  fetchUser: NULL_FUNCTION,
  fetchRating: NULL_FUNCTION,
  fetchMeReferral: NULL_FUNCTION,
  submitRating: NULL_FUNCTION,
  sendChatStart: NULL_FUNCTION,
  toggleModal: NULL_FUNCTION,
  copyTextWithToastr: NULL_FUNCTION,
  shouldShowMedias: true,
};

const ObservedPost = ({
  onChange,
  children,
  messageHeight,
  shouldShowMedias,
  isOnPostFeed,
  isIntersecting,
  mediaHeightInPostFeed,
  postRef,
}) => {
  const inViewOptions = React.useMemo(
    () => ({
      threshold: 0.01,
      onChange: inView => {
        onChange({
          isIntersecting: inView,
        });
      },
    }),
    [onChange]
  );
  const { ref, inView } = useInView(inViewOptions);

  const setRefs = React.useCallback(
    el => {
      ref(el);
      if (postRef) {
        postRef.current = el;
      }
    },
    [ref, postRef]
  );

  if (inView || isIntersecting || !messageHeight || !mediaHeightInPostFeed) {
    return (
      <StyledPost
        ref={setRefs}
        shouldShowMedias={shouldShowMedias}
        isOnPostFeed={isOnPostFeed}
      >
        {children}
      </StyledPost>
    );
  }

  return (
    <StyledPost
      ref={setRefs}
      shouldShowMedias={shouldShowMedias}
      messageHeight={messageHeight}
    />
  );
};

ObservedPost.propTypes = {
  onChange: PropTypes.func.isRequired,
  children: PropTypes.node,
  messageHeight: PropTypes.number,
  shouldShowMedias: PropTypes.bool,
  isOnPostFeed: PropTypes.bool,
  isIntersecting: PropTypes.bool.isRequired,
  mediaHeightInPostFeed: PropTypes.number,
  postRef: PropTypes.shape({
    current: PropTypes.any,
  }).isRequired,
};

ObservedPost.defaultProps = {
  children: null,
  messageHeight: null,
  shouldShowMedias: true,
  isOnPostFeed: false,
  isDetailLayout: false,
  mediaHeightInPostFeed: null,
};

const StyledPost = styled.div.attrs(({ shouldShowMedias, messageHeight }) => ({
  style: {
    minHeight: shouldShowMedias ? '300px' : 'unset',
    height: messageHeight ? `${messageHeight}px` : 'auto',
  },
}))`
  ${({ isOnPostFeed }) => {
    if (isOnPostFeed) {
      return css`
        padding-top: 8px;
        padding-bottom: 12px;
        border-bottom: 1px solid rgba(100, 100, 100, 0.15);
        ${media.mobile`
        padding-top: 8px;
        padding-bottom: 10px;
        `};
      `;
    }
  }}
`;

const UserRow = styled.div`
  display: flex;
  align-items: center;
  padding: 0px 16px 4px;
  ${media.mobile`
    padding: 0px 16px 4px;
  `};
`;

const AvatarCol = styled.div`
  flex: none;
  position: relative;
  margin-right: 12px;
  width: 36px;
  height: 36px;
`;

const UsernameCol = styled.div`
  flex: 1;
  max-width: calc(100% - 100px);
  font-size: 14px;
  overflow: hidden;
`;

const NicknameRow = styled.div`
  display: flex;
  align-items: center;
`;

const NicknameLink = styled(Link)`
  display: flex;
  align-items: center;
  flex: 0 1 auto;
  cursor: pointer;
  color: #fff;
  overflow: hidden;
  text-overflow: ellipsis;
  gap: 2px;
`;

const Nickname = styled.h1`
  ${TextEllipsis};
  display: inline-block;
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 150%; /* 21px */
  letter-spacing: 0.014px;
  ${media.mobile`
    font-size: 14px;
  `};
`;

const Username = styled.div`
  ${TextEllipsis};
  color: ${color.grey[300]};
  font-size: 16px;
  ${media.mobile`
    font-size: 14px;
  `};
  line-height: 150%; /* 21px */
  letter-spacing: 0.014px;
`;

const UserStatusIndicatorWrapper = styled.div`
  margin-left: 4px;
`;

const DateRow = styled.div`
  color: #646464;
  line-height: 150%; /* 18px */
  letter-spacing: 0.012px;
  font-size: 14px;
  ${media.mobile`
    font-size: 12px;
  `};
`;

const Divider = styled.span`
  margin: 0 4px;

  &::before {
    content: '・';
  }
`;

const ActionCol = styled.div`
  margin-left: auto;
  flex: none;
  display: flex;
  align-items: center;
`;

const ContextMenuButton = styled.button`
  outline: none;
  margin-left: 12px;
  border: 0;
  padding: 0;
  width: 28px;
  height: 28px;
  background: none;
  cursor: pointer;
  & > img {
    width: 100%;
    height: 100%;
  }
  ${media.mobile`
    width: 24px;
    height: 24px;
  `};
`;

const PinnedRow = styled.div`
  margin-bottom: 8px;
  padding: 0px 16px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const PinIcon = styled.img`
  margin-right: 4px;
  width: 16px;
  height: 16px;
  object-fit: contain;
`;

const PinText = styled.div`
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  color: ${color.neutral[55]};
  ${media.mobile`
    font-size: 12px;
  `};
`;

const CaptionRow = styled.div`
  padding: 0px 16px 12px;
`;

const Caption = styled.div.attrs(({ shouldShowMoreCaption, isOnMobile }) => ({
  style: {
    maxHeight: shouldShowMoreCaption
      ? 'unset'
      : `${
          (isOnMobile ? MOBILE_CAPTION_LINE_HEIGHT : CAPTION_LINE_HEIGHT) * 2
        }px`,
    lineHeight: `${
      isOnMobile ? MOBILE_CAPTION_LINE_HEIGHT : CAPTION_LINE_HEIGHT
    }px`,
  },
}))`
  display: block;
  word-break: break-word;
  font-size: ${CAPTION_FONT_SIZE}px;
  overflow: hidden;
  ${media.mobile`
    font-size: ${MOBILE_CAPTION_FONT_SIZE}px;
  `};
`;

const CaptionMoreButton = styled.button`
  ${ResetButtonStyle};
  margin-top: 4px;
  display: block;
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
  color: ${textColor.white};
  ${media.mobile`
    font-size: 12px;
  `};
`;

const TranslationToggler = styled.button`
  ${ResetButtonStyle};
  margin-top: 8px;
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
  color: #646464;
  ${media.mobile`
    font-size: 12px;
  `};
`;

const MediasWrapper = styled.div`
  position: relative;
  background-color: ${color.black};
`;

const Interactions = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 16px 0px;
`;

const Interaction = styled.button`
  ${ResetButtonStyle}
  flex: none;
  display: block;
  width: 32px;
  height: 32px;
  z-index: 2; // to render above swiper pagination
  opacity: 1;
  & + & {
    margin-left: 16px;
  }
  img {
    width: 100%;
    height: 100%;
  }
  ${media.mobile`
    width: 24px;
    height: 24px;
  `};
  :disabled {
    opacity: 0.5;
  }
`;

const Placeholder = styled.div`
  flex: auto;
`;

const MessageButton = styled.button`
  ${ResetButtonStyle}
  flex: none;
  display: flex;
  align-items: center;
  border: 1px solid ${color.white};
  border-radius: 4px;
  height: 28px;
  padding: 4px 12px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  color: white;
  z-index: 2; // to render above swiper pagination
  > img {
    margin-right: 4px;
    width: 20px;
    height: 20px;
    object-fit: contain;
  }
`;

const Metas = styled.div`
  padding: 8px 16px 0px;
  font-size: 14px;
  line-height: 1;
  color: #c8c8c8;
  ${media.mobile`
    font-size: 12px;
  `};
`;

const Meta = styled.div`
  display: inline-block;
  line-height: 1;
  color: #c8c8c8;
`;

const ActionButtons = styled.div`
  position: absolute;
  z-index: ${flixPlayerZIndex.unlockButton};
  left: 0px;
  right: 0px;
  bottom: 0px;
  padding: 0px 16px;
`;

const PostMediaWrapper = styled.div`
  ${({ isOnPostFeed }) => {
    if (isOnPostFeed) {
      return css`
        aspect-ratio: ${({ aspectRatio }) => aspectRatio};
        /* fallback */
        /* if not support aspect-ratio property, fit height to width ex. 1/1 ratio */
        @supports not (aspect-ratio: auto) {
          padding-top: 100%;
          position: relative;
          > div {
            position: absolute;
            top: 0;
            left: 0;
            bottom: 0;
            right: 0;
          }
        }
      `;
    }
  }}
`;

export default withTranslation()(withFetchTranslation(Post));
